import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import games from '../games/manifest';
import { clearHistory } from '../utils/useGameHistory';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ConfirmModal from '../components/ConfirmModal';
import { getTimerDuration, setTimerDuration, TIMER_OPTIONS } from '../utils/useTimer';
import { loadRandomPool, saveRandomPool } from '../utils/randomPool';
import { mechanicIcons } from '../utils/mechanicIcons';
import './Settings.css';

export default function Settings() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [resetMsg, setResetMsg] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [timerDuration, setTimerDurationState] = useState(getTimerDuration);
  const [randomPool, setRandomPool] = useState(() => {
    const saved = loadRandomPool();
    return saved || games.map((g) => g.id);
  });
  const [poolSearch, setPoolSearch] = useState('');
  const [poolFilter, setPoolFilter] = useState('all');
  const [, forceUpdate] = useState(0);

  const togglePoolGame = (id) => {
    setRandomPool((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      const result = next.length > 0 ? next : prev;
      saveRandomPool(result);
      return result;
    });
  };

  const allPoolSelected = randomPool.length === games.length;
  const nonePoolSelected = randomPool.length <= 1;
  const toggleAllPool = () => {
    if (allPoolSelected) return;
    const all = games.map((g) => g.id);
    saveRandomPool(all);
    setRandomPool(all);
  };
  const deselectAllPool = () => {
    if (nonePoolSelected) return;
    const first = [games[0].id];
    saveRandomPool(first);
    setRandomPool(first);
  };

  const handleReset = () => {
    clearHistory();
    localStorage.removeItem('biblika_max_points');
    setShowConfirm(false);
    setResetMsg(t('settings.resetDone'));
    forceUpdate((n) => n + 1);
    setTimeout(() => setResetMsg(''), 3000);
  };

  return (
    <div className="settings-page">
      <header className="settings-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          &#8592; {t('settings.back')}
        </button>
        <h2>{t('settings.title')}</h2>
      </header>

      <div className="settings-content">
        <section className="settings-section">
          <h3>{t('settings.general')}</h3>
          <div className="settings-settings">
            <div className="settings-setting-row">
              <span>{t('settings.language')}</span>
              <LanguageSwitcher variant="surface" />
            </div>
            <div className="settings-setting-row">
              <span>{t('settings.timer')}</span>
              <div className="settings-timer-options">
                {TIMER_OPTIONS.map((s) => (
                  <button
                    key={s}
                    className={`settings-timer-btn ${timerDuration === s ? 'active' : ''}`}
                    onClick={() => { setTimerDuration(s); setTimerDurationState(s); }}
                  >
                    {s === 0 ? 'OFF' : `${s}s`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="settings-section">
          <h3>{t('settings.randomPool')}</h3>
          <div className="settings-settings">
            <div className="settings-setting-row pool-header-row">
              <span>{t('settings.randomPoolDesc')}</span>
              <div className="settings-pool-actions">
                <button
                  className={`settings-pool-all-btn ${allPoolSelected ? 'active' : ''}`}
                  onClick={toggleAllPool}
                >
                  {t('settings.selectAll')}
                </button>
                <button
                  className={`settings-pool-all-btn ${nonePoolSelected ? 'active' : ''}`}
                  onClick={deselectAllPool}
                >
                  {t('settings.deselectAll')}
                </button>
              </div>
            </div>
            <input
              className="settings-pool-search"
              type="text"
              placeholder={t('settings.searchGames')}
              value={poolSearch}
              onChange={(e) => setPoolSearch(e.target.value)}
            />
            <div className="settings-pool-filters">
              {['all', 'quiz', 'binary', 'type', 'unscramble', 'sort', 'match', 'sequence', 'swipe', 'hangman', 'chain', 'puzzle'].map((f) => (
                <button
                  key={f}
                  className={`settings-pool-filter ${poolFilter === f ? 'active' : ''}`}
                  onClick={() => setPoolFilter(f)}
                >
                  {mechanicIcons[f]}
                  {f === 'all' ? t('progress.allMechanics') : t(`mechanic.${f}`)}
                </button>
              ))}
            </div>
            <div className="settings-pool-grid">
              {games
                .filter((g) => t(g.titleKey).toLowerCase().includes(poolSearch.toLowerCase()))
                .filter((g) => poolFilter === 'all' || g.mechanic === poolFilter)
                .map((g) => {
                const Icon = g.icon;
                const enabled = randomPool.includes(g.id);
                return (
                  <button
                    key={g.id}
                    className={`settings-pool-item ${enabled ? 'enabled' : ''}`}
                    onClick={() => togglePoolGame(g.id)}
                  >
                    {Icon && <Icon className="settings-pool-icon" />}
                    <span className="settings-pool-label">{t(g.titleKey)}</span>
                    {g.mechanic && <span className="settings-pool-mechanic">{mechanicIcons[g.mechanic]}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="settings-section">
          <h3>{t('settings.data')}</h3>
          <div className="settings-settings">
            <button className="settings-reset-btn" onClick={() => setShowConfirm(true)}>
              {t('settings.resetProgress')}
            </button>
            {resetMsg && <p className="settings-reset-msg">{resetMsg}</p>}
          </div>
        </section>
      </div>

      {showConfirm && (
        <ConfirmModal
          message={t('settings.resetConfirm')}
          confirmLabel={t('settings.confirm')}
          cancelLabel={t('settings.cancel')}
          onConfirm={handleReset}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}